import React, { useState, useEffect, useMemo } from 'react';
import { FORM_ERROR } from 'final-form';
import { Helmet as MetaTags } from 'react-helmet';
import { isNil } from 'ramda';
import { Auth } from '~/amplify';
import { navigate, RouteComponentProps } from '@gatsbyjs/reach-router';
import { Form, Field, EmailField } from '~/components/organism/Forms';
import Validation from '~/util/Validation';
import PasswordInputGroup from '~/components/page/Auth/components/PasswordInputGroup';
import FormUtils from '~/components/organism/FormUtils';
import Catalog from '~/Catalog';
import { useLocation } from '@gatsbyjs/reach-router';
import TEST_ID from '~/components/page/Auth/Register/index.testid';
import styled, { css } from 'styled-components';
import {
  SIGNUP_PARAMS,
  TAPFILLIATE_REF,
  setLocalStorageItem,
} from '~/util/localStorageKeys';
import { isValidSignupParams } from '~/hooks/useSignupParams';
import Button from '~/components/atom/Button';
import { isEmpty } from 'lodash';
import { reporter } from '~/hooks/useErrorReporter';
import { Body, Heading1 } from '~/components/atom/Typography';
import CheckboxLabel from '~/components/page/Auth/Register/components/CheckboxLabel/index';
import Checkbox from '~/components/molecule/Checkbox';
import useStoreUtmParams from '~/hooks/useStoreUtmParams';
import JustificationContainer from '~/components/atom/JustificationContainer';
import AuthFormWrapper from '../components/AuthFormWrapper';
import usePasswordValidation from '~/hooks/usePasswordValidation';
import Input from '~/components/molecule/Input';
import UnauthNav from '~/components/organism/UnauthNav';
import createPageTitle from '~/util/createPageTitle';
import useDelayedLoading from './hooks/useDelayedLoading';

type FormData = {
  name: string | null;
  password: string | null;
  passwordRepeat: string | null;
  email: string | null;
  phone: string | null;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

type UrlParams = {
  signupCode: string | null;
  accountName: string | null;
  couponCode: string | null;
  tapfillateRef: string | null;
};

const text = {
  noName: Catalog.noName,
  nameLabel: 'Voor- en achternaam',
  invalidEmail: Catalog.invalidEmail,

  unknownError: Catalog.genericUnknownErrorMessage,
  invalidPasswordFromCognito:
    'Het wachtwoord wordt niet geaccepteerd, probeer een ander wachtwoord',
  invalidEmailFromCognito:
    'Het e-mailadres wordt niet geaccepteerd, probeer een ander e-mailadres',
  title: 'Registreren',
  emailLabel: Catalog.emailLabel,
  phoneLabel: Catalog.phoneLabel,
  invalidPhone: Catalog.invalidPhone,
  noPhone: Catalog.noPhone,
  passwordLabel: Catalog.passwordLabel,
  passwordRepeatLabel: Catalog.passwordRepeatLabel,
  registerButton: 'Registreren',
  userAlreadyExists:
    'Er bestaat al een gebruiker met dit e-mailadres. Je kunt met dit e-mailadres inloggen.',
};

const initialValues: FormData = {
  name: null,
  password: null,
  passwordRepeat: null,
  email: null,
  phone: null,
};

const Register: React.FCC<RouteComponentProps> = () => {
  useStoreUtmParams();

  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [loading, startLoading, stopLoading] = useDelayedLoading();
  const [title, setTitle] = useState<string>(text.title);

  const { validatePassword, passwordConditions } = usePasswordValidation();
  const urlParams = useUrlParams();

  useEffect(() => {
    const { tapfillateRef, accountName, couponCode } = urlParams;

    if (tapfillateRef) {
      setLocalStorageItem(
        TAPFILLIATE_REF,
        JSON.stringify({ date: Date.now(), ref: tapfillateRef }),
      );
    }

    if (accountName != null) {
      setTitle(`Registreer ${accountName}`);
    }

    const signupParamsInURL = { couponCode };
    if (isValidSignupParams(signupParamsInURL)) {
      setLocalStorageItem(SIGNUP_PARAMS, JSON.stringify(signupParamsInURL));
    }
  }, [urlParams]);

  const validate = ({
    name,
    email,
    phone,
    password,
    passwordRepeat,
  }: FormData): FormErrors => {
    const errors: FormErrors = {};

    if (Validation.String.isEmpty(name)) {
      errors.name = text.noName;
    }
    if (Validation.String.isEmpty(phone)) {
      errors.phone = text.noPhone;
    }
    if (!Validation.Phone.isValid(phone)) {
      errors.phone = text.invalidPhone;
    }
    if (!Validation.Email.isValid(email)) {
      errors.email = text.invalidEmail;
    }

    const passwordErrors = validatePassword({ password, passwordRepeat });

    return { ...errors, ...passwordErrors };
  };

  const onFormSubmit = async ({
    name,
    email,
    password,
    phone,
  }: FormData): Promise<{ [FORM_ERROR]: string } | void> => {
    if (email === null || password === null) return;

    startLoading();
    const lowerCaseEmail = email.toLowerCase();

    const signupAttributes: Record<string, string> = {
      'custom:acceptedTNCDate': new Date().toISOString(),
    };

    if (name) {
      signupAttributes.name = name;
    }

    if (phone) {
      signupAttributes['custom:phone'] = phone;
    }

    const { signupCode } = urlParams;

    if (isValidSignupCode(signupCode)) {
      signupAttributes['custom:signupCode'] = signupCode!;
    }

    try {
      const result = await Auth.signUp({
        username: lowerCaseEmail,
        password,
        options: {
          userAttributes: signupAttributes,
        },
      });

      if (!result.isSignUpComplete) {
        void navigate('/verify', {
          state: { email: lowerCaseEmail },
        });
        return;
      }

      stopLoading();
      void navigate('/login');
      return;
    } catch (error: unknown) {
      stopLoading();
      return handleAuthError(error);
    }
  };

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(title)}</title>
        <meta
          name="description"
          content={`Maak een nieuw ${Catalog.companyName} account aan`}
        />
      </MetaTags>

      <UnauthNav
        buttons={[
          {
            label: 'Inloggen',
            onClick: () => navigate('/login'),
            appearance: 'primary',
            size: 'large',
          },
        ]}
      />

      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          margin={['l', null, 'base', null]}
          color={{ group: 'accent' }}
          size="xxxxl"
          skewed
        >
          {title}
        </Heading1>

        <Form
          initialValues={initialValues}
          onSubmit={onFormSubmit}
          validate={validate}
        >
          {({ handleSubmit, submitError, submitting, pristine }) => (
            <form
              id="register"
              onSubmit={handleSubmit}
              data-testid={TEST_ID.REGISTER_FORM}
            >
              {submitError ? (
                <Body
                  size="base"
                  margin={[null]}
                  color={{ group: 'danger', variant: 'light' }}
                  dataTestId={TEST_ID.REGISTER_ERROR_MESSAGE}
                >
                  {submitError}
                </Body>
              ) : null}
              <JustificationContainer align="center" margin={['m', null]}>
                <Field name="name">
                  {({ input, meta: { error, touched } }) => (
                    <Input
                      {...input}
                      autoFocus
                      width="100%"
                      size="large"
                      label={{
                        text: text.nameLabel,
                        color: { group: 'white' },
                      }}
                      disabled={submitting}
                      appearance="secondary"
                      externalErrors={
                        error && touched
                          ? [FormUtils.showError(error, touched)]
                          : undefined
                      }
                    />
                  )}
                </Field>
              </JustificationContainer>
              <JustificationContainer align="center" margin={['m', null]}>
                <EmailField name="email">
                  {({ input, meta: { error, touched } }) => (
                    <Input
                      {...input}
                      size="large"
                      width="100%"
                      label={{
                        text: text.emailLabel,
                        color: { group: 'white' },
                      }}
                      disabled={submitting}
                      externalErrors={
                        error && touched
                          ? [FormUtils.showError(error, touched)]
                          : undefined
                      }
                    />
                  )}
                </EmailField>
              </JustificationContainer>
              <JustificationContainer align="center" margin={['m', null]}>
                <Field name="phone">
                  {({ input, meta: { error, touched } }) => (
                    <Input
                      {...input}
                      width="100%"
                      size="large"
                      label={{
                        text: text.phoneLabel,
                        color: { group: 'white' },
                      }}
                      disabled={submitting}
                      externalErrors={
                        error && touched
                          ? [FormUtils.showError(error, touched)]
                          : undefined
                      }
                    />
                  )}
                </Field>
              </JustificationContainer>

              <PasswordInputGroup
                labelColor={{ group: 'white' }}
                passwordText={text.passwordLabel}
                repeatText={text.passwordRepeatLabel}
                passwordConditions={passwordConditions}
                submitting={submitting}
              />

              <JustificationContainer align="center" margin={['m', null]}>
                <Checkbox
                  onChange={() => setTermsAccepted(!termsAccepted)}
                  value={termsAccepted}
                  labelComponent={<CheckboxLabel />}
                  name="termsAccepted"
                  dataTestId={TEST_ID.TERMS_AND_CONDITIONS_CHECKBOX}
                />
              </JustificationContainer>
              <JustificationContainer align="center" justification="end">
                <FullWidthButton
                  appearance="primary"
                  type="submit"
                  loading={loading}
                  disabled={submitting || !termsAccepted || pristine || loading}
                  dataTestId={TEST_ID.SUBMIT_PASSWORD_BUTTON}
                  label={text.registerButton}
                  size="large"
                  style={{ justifyContent: 'center' }}
                />
              </JustificationContainer>
            </form>
          )}
        </Form>
      </AuthFormWrapper>
    </>
  );
};

const FullWidthButton = styled(Button)<{}>(
  () => css`
    justify-content: left;
    width: 100%;
  `,
);

const useUrlParams = (): UrlParams => {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      signupCode: params.get('signupCode'),
      accountName: params.get('accountName'),
      couponCode: params.get('couponCode'),
      tapfillateRef: params.get('ref'),
    };
  }, [location.search]);
};

const handleAuthError = (error: unknown): { [FORM_ERROR]: string } => {
  const err = error as any;

  if (err.name === 'UserLambdaValidationException') {
    return { [FORM_ERROR]: text.invalidEmailFromCognito };
  }

  if (err.name === 'InvalidPasswordException') {
    // report this to us, we should always validate the password beforehand so if we get this error something else went wrong
    reporter.captureException(err);
    return { [FORM_ERROR]: text.invalidPasswordFromCognito };
  }

  if (err.name === 'UsernameExistsException') {
    return { [FORM_ERROR]: text.userAlreadyExists };
  }

  reporter.captureException(err);
  return { [FORM_ERROR]: text.unknownError };
};

const isValidSignupCode = (signupCode: string | null | undefined): boolean => {
  if (isNil(signupCode)) return false;
  if (isEmpty(signupCode)) return false;
  return true;
};

export default Register;
